/**
 * 출석 스케줄 관리 페이지 (오너용)
 * 학부모 페이지와 동일한 UI/UX
 */

import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { API_BASE_URL } from '../../../config/api';
import { getStatusBadge, getMembershipBadge } from '../utils/memberBadges';
import MonthlyPlanOverview from '../components/MonthlyPlanOverview';
import MemberMonthlyScheduleView from '../components/MemberMonthlyScheduleView';
import ScheduleRegistrationSettings from '../components/ScheduleRegistrationSettings';
import ScheduleChangeTable from '../components/ScheduleChangeTable';

// 주간 스케줄용: 0=월요일, 6=일요일
const DAYS_OF_WEEK = ['월', '화', '수', '목', '금', '토', '일'];

/**
 * ⭐ 2026-08-15: 시/분 드롭다운.
 * 브라우저 기본 시간 입력(type="time")은 24시 이상을 못 받아서, 새벽까지 이어지는
 * 스케줄(예: 22:00~25:30 = 밤 10시~새벽 1시 반)을 아예 넣을 수 없었다.
 * 숫자만 보이면 헷갈리므로 24 이상엔 뜻을 같이 적는다.
 */
const hourLabel = (h: number) =>
  h < 24 ? String(h).padStart(2, '0')
    : h === 24 ? '24 (자정)'
      : `${h} (새벽 ${h - 24}시)`;

const HourMinutePicker: React.FC<{
  value: string;
  maxHour: number;
  onChange: (v: string) => void;
}> = ({ value, maxHour, onChange }) => {
  const [hs, ms] = (value || '00:00').split(':');
  const h = Math.min(Math.max(parseInt(hs, 10) || 0, 0), maxHour);
  // 마감 시각 정각(예: 경계 2시 → 26:00)이 마지막이라 그 시엔 분을 00 만 준다
  const minutes = h >= maxHour ? [0] : [0, 10, 20, 30, 40, 50];
  const m = h >= maxHour ? 0 : Math.floor((parseInt(ms, 10) || 0) / 10) * 10;
  const emit = (nh: number, nm: number) =>
    onChange(`${String(nh).padStart(2, '0')}:${String(nm).padStart(2, '0')}`);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <TextField select size="small" value={h} onChange={(e) => emit(Number(e.target.value), m)}
        sx={{ width: 116, minWidth: 116 }}>
        {Array.from({ length: maxHour + 1 }, (_, i) => i).map((v) => (
          <MenuItem key={v} value={v}>{hourLabel(v)}</MenuItem>
        ))}
      </TextField>
      <span style={{ fontSize: 13, color: '#6b7280' }}>시</span>
      <TextField select size="small" value={m} onChange={(e) => emit(h, Number(e.target.value))}
        sx={{ width: 72, minWidth: 72 }}>
        {minutes.map((v) => (
          <MenuItem key={v} value={v}>{String(v).padStart(2, '0')}</MenuItem>
        ))}
      </TextField>
      <span style={{ fontSize: 13, color: '#6b7280' }}>분</span>
    </Box>
  );
};

// 학원 스케줄용: 1=월요일, 7=일요일
const DAYS_MAP: { [key: number]: string } = {
  1: '월',
  2: '화',
  3: '수',
  4: '목',
  5: '금',
  6: '토',
  7: '일',
};

// 학생 타입
interface Student {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  seat_number?: string | number | null;
  // 아래는 회원 목록 API(/v1/owner/members)가 이미 내려주는 값들 — 따로 부르지 않는다
  member_number?: string;
  memberNumber?: string;
  status?: string;
  member_category?: string;
  has_schedule?: boolean;
  membershipType?: string;
  remainingTime?: any;
  expiryDate?: string;
  voucherName?: string;
  assigned_seat?: any;
  assigned_seat_code?: string;
}

// 학원 스케줄 타입
interface AcademySchedule {
  id: string;
  student_id: string;
  student_name?: string;
  academy_name: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  travel_minutes: number;
  grace_minutes: number;
  is_active: boolean;
  created_by_type?: string;
}

// 학원 스케줄 폼 타입
interface AcademyScheduleForm {
  academy_name: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  travel_minutes: number;
  grace_minutes: number;
}

// 예외 날짜 타입
interface ScheduleException {
  id: string;
  student_id: string;
  exception_date: string;
  reason: string;
  created_at: string;
}

// 주간 스케줄 타입
interface WeeklySchedule {
  id?: string;
  studentId: string;
  dayOfWeek: number;
  expectedCheckIn: string;
  expectedCheckOut: string;
  isActive: boolean;
}

const defaultAcademyForm: AcademyScheduleForm = {
  academy_name: '',
  days_of_week: [],
  start_time: '15:00',
  end_time: '17:00',
  travel_minutes: 15,
  grace_minutes: 10,
};

export default function AttendanceSchedulePage() {
  const [students, setStudents] = useState<Student[]>([]);
  // 승인 대기 중인 학생 — 목록에서 뱃지로 보여준다. 매장 전체를 한 번에 받는다.
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  // '조치 필요한 사람만' 걸러보기 (학습 분석의 [아직 안 쓴 학생만] 과 같은 방식)
  const [listFilter, setListFilter] = useState<'all' | 'pending' | 'none'>('all');
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [academySchedules, setAcademySchedules] = useState<AcademySchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchText, setSearchText] = useState('');

  // 스케줄 유형 상태
  const [scheduleType, setScheduleType] = useState<'weekly' | 'period'>('weekly');

  // 주간 스케줄 상태
  const [weeklySchedules, setWeeklySchedules] = useState<WeeklySchedule[]>([]);
  const [isWeeklySaving, setIsWeeklySaving] = useState(false);

  // 교시 스케줄 상태
  // ⭐ 2026-06-14: 학생이 입력한 시간범위 (요일별) — 교시와 별개로 "등록 시간" 표시용
  const [periodRanges, setPeriodRanges] = useState<Record<string, { start: string; end: string }[]>>({});
  // ⭐ 2026-06-14: 대기/승인 상태 (강제 허용 / 학부모 승인완료 표시)
  const [patternStatus, setPatternStatus] = useState<{
    has_pending: boolean; pending_id: string | null; pending_created_at: string | null;
    approved_at: string | null; approved_by_parent: boolean;
    pending_created_role?: string | null; pending_parent_consented?: boolean;
    pending_ranges?: Record<string, { start: string; end: string }[]> | null;
  } | null>(null);
  const [forcingApprove, setForcingApprove] = useState(false);
  // ⭐ 2026-08-21: 승인은 났는데 적용일이 아직 안 온 스케줄. 위 시간표(오늘 유효)와 분리해서 보여준다.
  const [upcoming, setUpcoming] = useState<{
    effective_from: string | null;
    ranges: Record<string, { start: string; end: string }[]> | null;
  } | null>(null);
  // ⭐ 2026-08-15: 매장이 정한 하루 경계 시각. 새벽까지 스케줄을 넣으려면 24·25·26 …
  //   (25시 = 새벽 1시) 표기가 필요하다. 브라우저 type="time" 은 24시 이상을 못 받아
  //   시/분 드롭다운으로 바꿨다.
  const [dayBoundaryHour, setDayBoundaryHour] = useState(2);
  // 🔴 2026-08-18: 운영자가 저장한 스케줄은 '저장한 날 당일부터' 적용된다.
  //    요일(월·화…)만 보여주면 "저장했는데 언제부터 반영되냐"고 오해한다(실제 문의).
  //    그 요일이 실제로 처음 적용되는 날짜를 같이 보여준다.
  const applyDates = useMemo(() => {
    const now = new Date();
    const logical = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (now.getHours() < dayBoundaryHour) logical.setDate(logical.getDate() - 1);
    const effFrom = new Date(logical);   // 당일 적용
    const fmt = (dt: Date) => `${dt.getMonth() + 1}/${dt.getDate()}`;
    const byDow: Record<string, string> = {};
    for (let d = 0; d < 7; d++) {
      const jsDow = (d + 1) % 7; // 0=월 → JS 규칙(0=일)
      const dt = new Date(effFrom);
      while (dt.getDay() !== jsDow) dt.setDate(dt.getDate() + 1);
      byDow[String(d)] = fmt(dt);
    }
    const DOW_KO = ['일', '월', '화', '수', '목', '금', '토'];
    return { effFrom: `${fmt(effFrom)}(${DOW_KO[effFrom.getDay()]})`, byDow };
  }, [dayBoundaryHour]);
  // ⭐ 2026-06-21: 운영자 시간 에디터 (요일별 시간범위, 멀티 구간) — 주 설정 방식
  const [timeEditor, setTimeEditor] = useState<Record<string, { start: string; end: string }[]>>({});
  const [savingTime, setSavingTime] = useState(false);
  // 단일 주간패턴이 오늘부터 무기한 적용 — 미리보기 기준월은 '이번 달'.
  // (UI 선택기 없음. API year_month 파라미터용 내부 값일 뿐, 어느 달이든 동일 패턴)
  const [periodYearMonth, setPeriodYearMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  // periodPlanInfo: 조회 메타 (현재는 UI 미표시, 디버깅용 유지)
  const [periodPlanInfo, setPeriodPlanInfo] = useState<any>(null);
  // 월간 계획 캘린더 강제 새로고침 키 (운영자 저장 후 변경 즉시 반영)
  const [monthlyPlanRefreshKey, setMonthlyPlanRefreshKey] = useState(0);

  // 예외 날짜 상태
  const [exceptions, setExceptions] = useState<ScheduleException[]>([]);
  const [showExceptionModal, setShowExceptionModal] = useState(false);
  const [exceptionDates, setExceptionDates] = useState<string[]>(['']);
  const [exceptionReason, setExceptionReason] = useState('');
  // 사유 종류. 종전엔 이 값이 없어서 서버가 영문 'absent' 로 저장했고,
  // 학생·학부모가 낸 한글 사유서와 값이 갈려 화면에서 겉돌았다. (2026-08-28)
  const [exceptionType, setExceptionType] = useState('결석');
  // 결석을 내려는데 그날 지각·조퇴·외출이 이미 있을 때 뜨는 확인 문구.
  // ⚠️ window.confirm 은 매장 환경에 따라 차단돼 '눌러도 반응 없음'이 되므로 쓰지 않는다.
  const [replacePrompt, setReplacePrompt] = useState<string | null>(null);

  // 학원 스케줄 모달 상태
  const [academyDialogOpen, setAcademyDialogOpen] = useState(false);
  const [academyForm, setAcademyForm] = useState<AcademyScheduleForm>(defaultAcademyForm);
  const [editingAcademyId, setEditingAcademyId] = useState<string | null>(null);
  const [academySaving, setAcademySaving] = useState(false);

  // 메모 상태
  const [memos, setMemos] = useState<Array<{
    id: string; content: string; category: string;
    author_name: string; created_at: string; is_pinned: boolean;
  }>>([]);
  const [memosTotal, setMemosTotal] = useState(0);
  const [isLoadingMemos, setIsLoadingMemos] = useState(false);
  const [newMemoContent, setNewMemoContent] = useState('');
  const [showAllMemos, setShowAllMemos] = useState(false);

  const MEMO_CATS: Record<string, string> = {
    general: '일반', academic: '학업', behavior: '행동',
    health: '건강', family: '가정', other: '기타',
  };

  useEffect(() => {
    loadStudents();
    loadScheduleType();
  }, []);

  const loadScheduleType = async () => {
    try {
      const token = getAuthToken();
      const orgId = localStorage.getItem('organization_id');
      if (!orgId) return;

      const response = await fetch(`${API_BASE_URL}/v1/owner/organization/${orgId}/schedule-type`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;

      const data = await response.json();
      setScheduleType(data.schedule_type || 'weekly');
    } catch (err) {
      console.error('스케줄 유형 로드 실패:', err);
    }
  };

  useEffect(() => {
    if (selectedStudentId) {
      loadAcademySchedules();
      if (scheduleType === 'period') {
        loadPeriodData();
      } else {
        loadWeeklySchedules();
      }
    }
  }, [selectedStudentId, scheduleType]);

  // 월 셀렉터 변경 시 그 달 plan 다시 로드
  useEffect(() => {
    if (selectedStudentId && scheduleType === 'period') {
      loadPeriodData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodYearMonth]);

  const getAuthToken = () => {
    return localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  };

  // 주간 스케줄 로드
  const loadWeeklySchedules = async () => {
    if (!selectedStudentId) return;

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/student-schedules/student/${selectedStudentId}/study-schedules`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let dbSchedules: any[] = [];
      if (response.ok) {
        const data = await response.json();
        dbSchedules = data.data || data || [];
      }

      const scheduleMap: { [key: number]: WeeklySchedule } = {};
      (dbSchedules || []).forEach((s: any) => {
        const dayOfWeek = s.dayOfWeek ?? s.day_of_week;
        scheduleMap[dayOfWeek] = {
          id: s.id,
          studentId: s.studentId || s.student_id || selectedStudentId,
          dayOfWeek: dayOfWeek,
          expectedCheckIn: s.expectedCheckIn || s.expected_check_in || '09:00',
          expectedCheckOut: s.expectedCheckOut || s.expected_check_out || '22:00',
          isActive: s.isActive ?? s.is_active ?? true,
        };
      });

      const allSchedules: WeeklySchedule[] = [];
      for (let i = 0; i < 7; i++) {
        allSchedules.push(
          scheduleMap[i] || {
            studentId: selectedStudentId,
            dayOfWeek: i,
            expectedCheckIn: '09:00',
            expectedCheckOut: '22:00',
            isActive: i >= 0 && i <= 4, // 월~금 기본 활성화
          }
        );
      }
      setWeeklySchedules(allSchedules);
    } catch (err) {
      console.error('주간 스케줄 조회 실패:', err);
      // 기본값
      const defaults: WeeklySchedule[] = [];
      for (let i = 0; i < 7; i++) {
        defaults.push({
          studentId: selectedStudentId,
          dayOfWeek: i,
          expectedCheckIn: '09:00',
          expectedCheckOut: '22:00',
          isActive: i >= 0 && i <= 4,
        });
      }
      setWeeklySchedules(defaults);
    }
  };

  // 주간 스케줄 변경
  const handleWeeklyChange = (dayOfWeek: number, field: keyof WeeklySchedule, value: any) => {
    setWeeklySchedules((prev) =>
      prev.map((s) => (s.dayOfWeek === dayOfWeek ? { ...s, [field]: value } : s))
    );
  };

  // 주간 스케줄 저장
  const handleSaveWeeklySchedules = async () => {
    if (!selectedStudentId) return;
    setIsWeeklySaving(true);
    setError('');
    setSuccess('');

    try {
      const token = getAuthToken();

      // 각 요일별로 개별 저장 (오너 API 형식)
      for (const schedule of weeklySchedules) {
        const payload = {
          student_id: selectedStudentId,
          day_of_week: schedule.dayOfWeek,
          expected_check_in: schedule.expectedCheckIn.split(':').length >= 3 ? schedule.expectedCheckIn : `${schedule.expectedCheckIn}:00`,
          expected_check_out: schedule.expectedCheckOut.split(':').length >= 3 ? schedule.expectedCheckOut : `${schedule.expectedCheckOut}:00`,
          is_active: schedule.isActive,
        };

        const response = await fetch(`${API_BASE_URL}/v1/owner/student-schedules/study-schedules`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.detail || `${DAYS_OF_WEEK[schedule.dayOfWeek]}요일 스케줄 저장 실패`);
        }
      }

      setSuccess('주간 스케줄이 저장되었습니다.');
      setTimeout(() => setSuccess(''), 3000);
      await loadWeeklySchedules();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsWeeklySaving(false);
    }
  };

  const loadStudents = async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/v1/owner/members?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setStudents(data.members || []);
        // 승인 대기(월간 계획 + 주간 패턴)를 한 번에 받아 둔다
        try {
          // 🔴 이 라우터는 prefix 가 '/api/v1/monthly-plans' 다. API_BASE_URL 뒤에
          //    '/v1/...' 로 붙이면 404 가 나는데, 실패를 삼키게 해 둬서
          //    배지가 조용히 안 뜬다(2026-09-10 배포 전 점검에서 잡음).
          //    라우터마다 등록 경로가 다르니 새 주소를 쓸 땐 반드시 한 번 찍어볼 것.
          const pres = await fetch(`${API_BASE_URL.replace(/\/api$/, '')}/api/v1/monthly-plans/pending/list`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (pres.ok) {
            const pdata = await pres.json();
            const rows: any[] = Array.isArray(pdata) ? pdata : (pdata?.data ?? pdata?.plans ?? []);
            setPendingIds(new Set(rows.map((r: any) => String(r.student_id))));
          }
          // 승인·거절·저장 뒤엔 늘 이 목록을 다시 불러온다 → 여기서 한 번 알리면 빠지는 곳이 없다
          window.dispatchEvent(new Event('schedule-counts-changed'));
        } catch {
          // 뱃지는 부가 정보다 — 실패해도 목록은 그대로 보여준다
        }
      }
    } catch (err) {
      console.error('학생 목록 조회 실패:', err);
      setError('학생 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAcademySchedules = async () => {
    if (!selectedStudentId) return;

    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/v1/owner/academy-schedules/student/${selectedStudentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setAcademySchedules(data.data || []);
      }
    } catch (err) {
      console.error('학원 스케줄 조회 실패:', err);
    }
  };

  // 학생 스케줄 로드 (시간 범위 + 승인 상태)
  const loadPeriodData = async () => {
    if (!selectedStudentId) return;
    const token = getAuthToken();

    try {
      const schRes = await fetch(
        `${API_BASE_URL}/v1/owner/student-schedules/student/${selectedStudentId}/period-schedules?year_month=${periodYearMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (schRes.ok) {
        const schData = await schRes.json();
        setPeriodRanges(schData.ranges || {});
        if (typeof schData.day_boundary_hour === 'number') {
          setDayBoundaryHour(schData.day_boundary_hour);
        }
        setPatternStatus(schData.pattern_status || null);
        setUpcoming(schData.upcoming?.effective_from ? schData.upcoming : null);
        setPeriodPlanInfo({ source: schData.source, ...(schData.plan || {}) });
      } else {
        setPeriodRanges({});
        setPatternStatus(null);
        setUpcoming(null);
        setPeriodPlanInfo(null);
      }
    } catch (err) {
      console.error('스케줄 조회 실패:', err);
    }
  };

  // ⭐ 2026-06-14: 운영자 강제 허용 (대기중 패턴 승인)
  const handleForceApprove = async () => {
    if (!selectedStudentId) return;
    if (!window.confirm(
      patternStatus?.pending_parent_consented
        ? '학부모가 동의한 요청입니다. 승인하시겠습니까?\n\n· 오늘까지는 지금 스케줄 그대로이고, 내일부터 바뀝니다.\n· 오늘 당장 고쳐야 하면 아래 시간 설정에서 직접 저장해 주세요 — 그건 오늘부터 적용됩니다.'
        : '아직 학부모 동의가 없습니다. 원장님 판단으로 승인하시겠습니까?\n\n· 오늘까지는 지금 스케줄 그대로이고, 내일부터 바뀝니다.\n· 오늘 당장 고쳐야 하면 아래 시간 설정에서 직접 저장해 주세요 — 그건 오늘부터 적용됩니다.'
    )) return;
    setForcingApprove(true);
    try {
      const token = getAuthToken();
      const res = await fetch(
        `${API_BASE_URL}/v1/owner/student-schedules/student/${selectedStudentId}/approve-pending`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const body = await res.json().catch(() => ({} as any));
        alert(`✅ ${body?.message || '승인 완료 — 내일부터 출결 판정에 적용됩니다.'}`);
        await loadPeriodData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`승인 실패: ${err.detail || res.status}`);
      }
    } finally {
      setForcingApprove(false);
    }
  };

  // ⭐ 시간 에디터를 현재 등록 시간(periodRanges)으로 동기화
  useEffect(() => {
    const copy: Record<string, { start: string; end: string }[]> = {};
    for (const k of Object.keys(periodRanges || {})) {
      copy[k] = (periodRanges[k] || []).map((r) => ({ start: r.start, end: r.end }));
    }
    setTimeEditor(copy);
  }, [periodRanges]);

  const addTimeRange = (day: string) => {
    setTimeEditor((prev) => ({ ...prev, [day]: [...(prev[day] || []), { start: '09:00', end: '22:00' }] }));
  };
  const removeTimeRange = (day: string, idx: number) => {
    setTimeEditor((prev) => ({ ...prev, [day]: (prev[day] || []).filter((_, i) => i !== idx) }));
  };
  const updateTimeRange = (day: string, idx: number, field: 'start' | 'end', value: string) => {
    setTimeEditor((prev) => ({
      ...prev,
      [day]: (prev[day] || []).map((r, i) => (i === idx ? { ...r, [field]: value } : r)),
    }));
  };
  const handleSaveTimeSchedule = async () => {
    if (!selectedStudentId) return;
    // 시작<종료 검증
    for (const [k, rs] of Object.entries(timeEditor)) {
      for (const r of rs) {
        if (r.start && r.end && r.start >= r.end) {
          alert(`${['월','화','수','목','금','토','일'][Number(k)]}요일: 시작 시간이 종료보다 빠르거나 같습니다 (${r.start}~${r.end})`);
          return;
        }
      }
    }
    // 같은 요일 시간 겹침/중복 검증 — 같은 요일에 겹치거나 같은 시간대는 등록 불가
    for (const [k, rs] of Object.entries(timeEditor)) {
      const valid = rs
        .filter((r) => r.start && r.end)
        .slice()
        .sort((a, b) => a.start.localeCompare(b.start));
      for (let i = 1; i < valid.length; i++) {
        if (valid[i].start < valid[i - 1].end) {
          alert(
            `${['월','화','수','목','금','토','일'][Number(k)]}요일: 시간이 겹칩니다 ` +
            `(${valid[i - 1].start}~${valid[i - 1].end} ↔ ${valid[i].start}~${valid[i].end}).\n` +
            `같은 요일에 중복되거나 겹치는 시간은 등록할 수 없습니다.`
          );
          return;
        }
      }
    }
    // 🔴 2026-08-21: **원장이 실제로 고친 요일만** 보낸다.
    //   서버는 "요청에 없는 요일은 기존값 보존"으로 설계돼 있는데, 여기서 7요일을 통째로
    //   보내고 있었다. 화면이 '오늘 유효한 시간표'를 보여주므로, 통째로 보내면 원장이
    //   아무것도 안 고치고 저장만 눌러도 승인된 '적용 예정' 변경이 오늘 값으로 덮여 사라진다.
    //   (건드리지 않은 요일은 서버가 미래 확정분까지 포함한 베이스에서 그대로 살린다)
    const sameDay = (a?: { start: string; end: string }[], b?: { start: string; end: string }[]) => {
      const na = (a || []).filter((r) => r.start && r.end);
      const nb = (b || []).filter((r) => r.start && r.end);
      if (na.length !== nb.length) return false;
      const key = (rs: typeof na) => rs.map((r) => `${r.start}-${r.end}`).sort().join('|');
      return key(na) === key(nb);
    };
    const changed: Record<string, { start: string; end: string }[]> = {};
    for (const d of ['0', '1', '2', '3', '4', '5', '6']) {
      if (!sameDay(timeEditor[d], periodRanges[d])) changed[d] = timeEditor[d] || [];
    }
    if (Object.keys(changed).length === 0) {
      alert('바뀐 내용이 없습니다.');
      return;
    }
    setSavingTime(true);
    try {
      const token = getAuthToken();
      const res = await fetch(
        `${API_BASE_URL}/v1/owner/student-schedules/student/${selectedStudentId}/time-schedule`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ schedules: changed }),
        }
      );
      if (res.ok) {
        const body = await res.json().catch(() => ({} as any));
        alert(`✅ ${body?.message || '저장되었습니다. 오늘부터 출결 판정에 적용됩니다.'}`);
        await loadPeriodData();
        // ⭐ 2026-07-21: 월간 계획 달력도 다시 불러온다.
        //    이 한 줄이 빠져 있어서 "저장은 됐는데 달력에 안 보인다"로 보였다(새로고침하면 나옴).
        //    교시 저장 쪽에는 이미 있던 처리 — 시간 저장 쪽만 누락돼 있었다.
        setMonthlyPlanRefreshKey((k) => k + 1);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(`저장 실패: ${err.detail || res.status}`);
      }
    } finally {
      setSavingTime(false);
    }
  };

  // 학원 스케줄 다이얼로그 열기
  const openAcademyDialog = (schedule?: AcademySchedule) => {
    if (schedule) {
      setEditingAcademyId(schedule.id);
      setAcademyForm({
        academy_name: schedule.academy_name,
        days_of_week: schedule.days_of_week,
        start_time: schedule.start_time.slice(0, 5),
        end_time: schedule.end_time.slice(0, 5),
        travel_minutes: schedule.travel_minutes || 15,
        grace_minutes: schedule.grace_minutes,
      });
    } else {
      setEditingAcademyId(null);
      setAcademyForm(defaultAcademyForm);
    }
    setAcademyDialogOpen(true);
  };

  const closeAcademyDialog = () => {
    setAcademyDialogOpen(false);
    setEditingAcademyId(null);
    setAcademyForm(defaultAcademyForm);
  };

  const handleAcademyFormChange = (field: keyof AcademyScheduleForm, value: any) => {
    setAcademyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDayToggle = (day: number) => {
    setAcademyForm((prev) => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter((d) => d !== day)
        : [...prev.days_of_week, day].sort((a, b) => a - b),
    }));
  };

  // 학원 스케줄 저장
  const saveAcademySchedule = async () => {
    if (!selectedStudentId || !academyForm.academy_name || academyForm.days_of_week.length === 0) {
      setError('학원명과 요일을 입력해주세요.');
      return;
    }

    setAcademySaving(true);
    setError('');

    try {
      const token = getAuthToken();
      const payload = {
        student_id: selectedStudentId,
        academy_name: academyForm.academy_name,
        days_of_week: academyForm.days_of_week,
        start_time: academyForm.start_time,
        end_time: academyForm.end_time,
        travel_minutes: academyForm.travel_minutes,
        grace_minutes: academyForm.grace_minutes,
      };

      let response;
      if (editingAcademyId) {
        response = await fetch(`${API_BASE_URL}/v1/owner/academy-schedules/${editingAcademyId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE_URL}/v1/owner/academy-schedules`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || '학원 스케줄 저장 실패');
      }

      setSuccess(editingAcademyId ? '학원 스케줄이 수정되었습니다.' : '학원 스케줄이 추가되었습니다.');
      closeAcademyDialog();
      loadAcademySchedules();
    } catch (err: any) {
      setError(err.message || '학원 스케줄 저장에 실패했습니다.');
    } finally {
      setAcademySaving(false);
    }
  };

  // 학원 스케줄 삭제
  const deleteAcademySchedule = async (scheduleId: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/v1/owner/academy-schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('학원 스케줄 삭제 실패');
      }

      setSuccess('학원 스케줄이 삭제되었습니다.');
      loadAcademySchedules();
    } catch (err: any) {
      setError(err.message || '학원 스케줄 삭제에 실패했습니다.');
    }
  };

  // 예외 날짜 로드
  const loadExceptions = async (studentId: string) => {
    try {
      const token = getAuthToken();
      const res = await fetch(
        `${API_BASE_URL}/v1/owner/schedule-exceptions?student_id=${studentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setExceptions(data);
      }
    } catch (err) {
      console.error('예외 날짜 로드 실패:', err);
    }
  };

  // 예외 날짜 생성
  const createExceptions = async (replaceExisting = false) => {
    const validDates = exceptionDates.filter((d) => d.trim() !== '');
    if (validDates.length === 0 || !exceptionReason.trim()) {
      setError('날짜와 사유를 모두 입력해주세요.');
      return;
    }

    try {
      const token = getAuthToken();
      const body = validDates.map((d) => ({
        student_id: selectedStudentId,
        exception_date: d,
        exception_type: exceptionType,
        reason: exceptionReason.trim(),
        replace_existing: replaceExisting,
      }));
      const res = await fetch(`${API_BASE_URL}/v1/owner/schedule-exceptions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        setSuccess('예외 날짜가 등록되었습니다.');
        setTimeout(() => setSuccess(''), 3000);
        loadExceptions(selectedStudentId!);
        setShowExceptionModal(false);
        setExceptionDates(['']);
        setExceptionReason('');
        setExceptionType('결석');
        setReplacePrompt(null);
      } else if (res.status === 409) {
        // 그날 다른 사유서가 있어 결석으로 바꿔야 하는 상황.
        // 막지 않고 "바꾸시겠습니까?"를 띄운 뒤 원장이 누르면 그대로 진행한다.
        const errData = await res.json();
        setReplacePrompt(errData.detail || '이미 다른 사유서가 있습니다. 결석으로 바꾸시겠습니까?');
      } else {
        const errData = await res.json();
        throw new Error(errData.detail || '예외 날짜 등록 실패');
      }
    } catch (err: any) {
      setError(err.message || '예외 날짜 등록에 실패했습니다.');
    }
  };

  // 예외 날짜 삭제
  const deleteException = async (id: string) => {
    if (!window.confirm('이 예외 날짜를 삭제하시겠습니까?')) return;

    try {
      const token = getAuthToken();
      await fetch(`${API_BASE_URL}/v1/owner/schedule-exceptions/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('예외 날짜가 삭제되었습니다.');
      setTimeout(() => setSuccess(''), 3000);
      loadExceptions(selectedStudentId!);
    } catch (err: any) {
      setError(err.message || '예외 날짜 삭제에 실패했습니다.');
    }
  };

  // 날짜 포맷 헬퍼
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // 요일 이름 헬퍼
  const getDayName = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    return `${dayNames[d.getDay()]}요일`;
  };

  // 메모 로드
  const loadMemos = async (studentId: string, offset = 0) => {
    setIsLoadingMemos(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/v1/owner/member-memos?student_id=${studentId}&limit=20&offset=${offset}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          if (offset === 0) {
            setMemos(result.data.memos);
          } else {
            setMemos(prev => [...prev, ...result.data.memos]);
          }
          setMemosTotal(result.data.total);
        }
      }
    } catch (err) {
      console.error('메모 로드 실패:', err);
    } finally {
      setIsLoadingMemos(false);
    }
  };

  // 학생 선택 시 메모 + 예외 날짜 로드
  useEffect(() => {
    if (selectedStudentId) {
      loadMemos(selectedStudentId);
      loadExceptions(selectedStudentId);
      setShowAllMemos(false);
      setNewMemoContent('');
    }
  }, [selectedStudentId]);

  // 메모 작성
  const handleMemoSubmit = async () => {
    if (!newMemoContent.trim() || !selectedStudentId) return;
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/owner/member-memos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: selectedStudentId, content: newMemoContent.trim(), category: 'general' }),
      });
      if (response.ok) {
        setNewMemoContent('');
        loadMemos(selectedStudentId);
      }
    } catch (err) {
      console.error('메모 작성 실패:', err);
    }
  };

  // 메모 삭제
  const handleMemoDelete = async (memoId: string) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;
    try {
      const token = getAuthToken();
      await fetch(`${API_BASE_URL}/api/v1/owner/member-memos/${memoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      loadMemos(selectedStudentId);
    } catch (err) {
      console.error('메모 삭제 실패:', err);
    }
  };

  // 요일 포맷
  const formatDays = (days: number[]) => {
    return days.map((d) => DAYS_MAP[d]).join(', ');
  };

  const selectedStudent = students.find((s) => s.id === selectedStudentId);
  const [activeTab, setActiveTab] = useState<'schedule' | 'monthly' | 'reg_settings'>('schedule');

  // ⭐ 2026-10-10 대표 지시 — 탭마다 승인 대기 숫자.
  //   스케줄 관리 = 주간 스케줄 변경 요청 / 월간 계획 = 월간 계획 승인 대기 (학생 수).
  //   사이드바 '스케줄 설정' 숫자는 이 둘의 합이다(useOwnerLayout 이 같은 주소를 부른다).
  const [pendingCounts, setPendingCounts] = useState({ weekly_pattern: 0, monthly_plan: 0 });
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL.replace(/\/api$/, '')}/api/v1/monthly-plans/pending/count`,
          { headers: { Authorization: `Bearer ${getAuthToken()}` } },
        );
        if (res.ok) {
          const d = await res.json();
          setPendingCounts({
            weekly_pattern: Number(d?.weekly_pattern ?? 0),
            monthly_plan: Number(d?.monthly_plan ?? 0),
          });
        }
      } catch {
        // 숫자는 부가 정보 — 실패해도 화면은 그대로
      }
    };
    load();
    window.addEventListener('schedule-counts-changed', load);
    return () => window.removeEventListener('schedule-counts-changed', load);
  }, []);

  const tabBadge = (n: number, active: boolean) =>
    n > 0 ? (
      <span
        className={`ml-1.5 inline-flex min-w-[20px] justify-center rounded-full px-1.5 text-xs font-bold ${
          active ? 'bg-white text-blue-600' : 'bg-red-500 text-white'
        }`}
      >
        {n}
      </span>
    ) : null;

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        스케줄 관리
      </Typography>

      {/* 탭 전환: 스케줄 관리 / 월간 계획 */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('schedule')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            activeTab === 'schedule' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          스케줄 관리
          {tabBadge(pendingCounts.weekly_pattern, activeTab === 'schedule')}
        </button>
        <button
          onClick={() => setActiveTab('monthly')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            activeTab === 'monthly' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          월간 계획
          {tabBadge(pendingCounts.monthly_plan, activeTab === 'monthly')}
        </button>
        <button
          onClick={() => setActiveTab('reg_settings')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${
            activeTab === 'reg_settings' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          등록 관리
        </button>
      </div>

      {/* 월간 계획 탭 */}
      {activeTab === 'monthly' && <MonthlyPlanOverview />}

      {/* 등록 관리 탭 */}
      {activeTab === 'reg_settings' && <ScheduleRegistrationSettings />}

      {/* 기존 스케줄 관리 탭 */}
      {activeTab === 'schedule' && (
      <>
      <Chip
        label={scheduleType === 'period' ? '교시 스케줄 모드' : '주간 스케줄 모드'}
        color={scheduleType === 'period' ? 'primary' : 'warning'}
        size="small"
        sx={{ mb: 3, fontWeight: 600 }}
      />

      {/* 학생 검색/선택 */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          label="학생 검색"
          placeholder="이름으로 검색..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={async (e) => {
            if (e.key === 'Enter' && !(e.nativeEvent as any).isComposing && searchText.trim()) {
              const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
              try {
                const res = await fetch(
                  `${API_BASE_URL}/v1/owner/members?search=${encodeURIComponent(searchText.trim())}&limit=5`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                if (res.ok) {
                  const data = await res.json();
                  const members = data.members || [];
                  if (members.length > 0) {
                    const exact = members.find((m: any) => m.name === searchText.trim());
                    const s = exact || members[0];
                    setSelectedStudentId(s.id);
                    setSearchText('');
                  }
                }
              } catch {}
            }
          }}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* 선택된 학생 표시 */}
        {selectedStudent && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                size="small"
                onClick={() => {
                  setSelectedStudentId('');
                  setSearchText('');
                }}
              >
                다른 학생
              </Button>
            }
          >
            선택된 학생: <strong>{selectedStudent.name}</strong> {selectedStudent.email && `(${selectedStudent.email})`}
          </Alert>
        )}

        {/* 🔴 학생 목록은 '항상' 보여준다 (2026-09-10 대표 지시)
               종전엔 검색어를 쳐야만 목록이 나와서, 이름을 정확히 모르면 들어갈 방법이
               없었다. 표 모양·컬럼은 [학습 분석]과 같게 맞춘다 — 원장이 두 화면을
               오갈 때 같은 자리에서 같은 것을 보게 된다.
               '코멘트' 자리에 '스케줄'(등록됨 / 미등록 / 승인 대기)이 들어간다. */}
        {!selectedStudent && (() => {
          const q = searchText.trim().toLowerCase();
          const base = q
            ? students.filter(
                (st) =>
                  st.name.toLowerCase().includes(q) ||
                  ((st.memberNumber || st.member_number) &&
                    String(st.memberNumber || st.member_number).toLowerCase().includes(q)),
              )
            : students;
          const list = base.filter((st) => {
            if (listFilter === 'pending') return pendingIds.has(String(st.id));
            if (listFilter === 'none') return !st.has_schedule && !pendingIds.has(String(st.id));
            return true;
          });
          const pendingCount = students.filter((st) => pendingIds.has(String(st.id))).length;
          const noneCount = students.filter(
            (st) => !st.has_schedule && !pendingIds.has(String(st.id)),
          ).length;

          return (
            <Box sx={{ mt: 1 }}>
              {/* 조치가 필요한 사람 — 학습 분석의 노란 띠와 같은 자리 */}
              {(pendingCount > 0 || noneCount > 0) && (
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                  <span className="text-sm text-amber-900">
                    {pendingCount > 0 && <b>승인 대기 {pendingCount}명</b>}
                    {pendingCount > 0 && noneCount > 0 && ' · '}
                    {noneCount > 0 && <>스케줄 미등록 {noneCount}명</>}
                  </span>
                  <span className="ml-auto flex gap-1">
                    {pendingCount > 0 && (
                      <button
                        onClick={() => setListFilter(listFilter === 'pending' ? 'all' : 'pending')}
                        className={`px-2 py-1 text-xs rounded border transition-colors ${
                          listFilter === 'pending'
                            ? 'bg-amber-600 text-white border-amber-600'
                            : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'
                        }`}
                      >
                        승인 대기만
                      </button>
                    )}
                    {noneCount > 0 && (
                      <button
                        onClick={() => setListFilter(listFilter === 'none' ? 'all' : 'none')}
                        className={`px-2 py-1 text-xs rounded border transition-colors ${
                          listFilter === 'none'
                            ? 'bg-amber-600 text-white border-amber-600'
                            : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'
                        }`}
                      >
                        미등록만
                      </button>
                    )}
                  </span>
                </div>
              )}

              <div className="text-xs text-gray-500 mb-1">
                {q || listFilter !== 'all' ? `${list.length}명` : `총 ${list.length}명 — 이름을 눌러 선택하세요`}
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg" style={{ maxHeight: 420, overflowY: 'auto' }}>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">이름</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">스케줄</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">상태</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">구분</th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">이용권</th>
                      <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">좌석번호</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {list.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-400">
                          {q ? '검색 결과가 없습니다.' : '해당하는 학생이 없습니다.'}
                        </td>
                      </tr>
                    ) : (
                      list.map((st) => (
                        <tr
                          key={st.id}
                          className="hover:bg-blue-50 cursor-pointer"
                          onClick={() => {
                            setSelectedStudentId(st.id);
                            setSearchText('');
                          }}
                        >
                          {/* 좁은 화면에서는 회원번호를 이름 아래로 (학습 분석과 동일) */}
                          <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2">
                              <div className="text-sm font-medium text-gray-900">{st.name}</div>
                              <span className="inline-flex w-fit items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {st.memberNumber || st.member_number || 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {pendingIds.has(String(st.id)) ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                                ⏳ 승인 대기
                              </span>
                            ) : st.has_schedule ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                등록됨
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                                미등록
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {st.status ? getStatusBadge(st.status as any) : <span className="text-sm text-gray-400">-</span>}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {st.member_category ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {st.member_category}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="hidden md:table-cell px-4 py-3 whitespace-nowrap">
                            {getMembershipBadge(
                              st.membershipType as any,
                              st.remainingTime as any,
                              st.expiryDate as any,
                              st.voucherName as any,
                            )}
                          </td>
                          <td className="hidden lg:table-cell px-4 py-3 whitespace-nowrap">
                            {st.assigned_seat ? (
                              <span className="text-sm font-medium text-purple-600">
                                {st.assigned_seat_code || `좌석 ${st.assigned_seat}`}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </Box>
          );
        })()}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !selectedStudentId ? (
        <Alert severity="info">학생을 선택해주세요.</Alert>
      ) : (
        <>
          {/* ========== 학원 스케줄 섹션 ========== */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SchoolIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  학원 스케줄
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openAcademyDialog()}
                size="small"
              >
                학원 추가
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              학원 외출/복귀 시간을 설정하면, 예정 시간에 외출하지 않거나 복귀하지 않을 경우 알림을 받을 수 있습니다.
            </Typography>

            {academySchedules.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                등록된 학원 스케줄이 없습니다. "학원 추가" 버튼을 눌러 학원 스케줄을 등록해주세요.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {academySchedules.map((schedule) => (
                  <Paper
                    key={schedule.id}
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {schedule.academy_name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={formatDays(schedule.days_of_week)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`${schedule.start_time.slice(0, 5)} ~ ${schedule.end_time.slice(0, 5)}`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`이동 ${schedule.travel_minutes || 15}분`}
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                        <Chip
                          label={`여유 ${schedule.grace_minutes}분`}
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.secondary' }}
                        />
                        <Chip
                          label={schedule.created_by_type === 'staff' ? '직원등록' : '학부모등록'}
                          size="small"
                          color={schedule.created_by_type === 'staff' ? 'secondary' : 'default'}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <IconButton size="small" onClick={() => openAcademyDialog(schedule)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteAcademySchedule(schedule.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            )}
          </Paper>

          {/* ========== 메모 섹션 ========== */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>📝 메모</Typography>
                {memosTotal > 0 && (
                  <Chip label={`${memosTotal}건`} size="small" color="default" />
                )}
              </Box>
            </Box>

            {/* 메모 작성 */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="메모를 입력하세요..."
                value={newMemoContent}
                onChange={(e) => setNewMemoContent(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleMemoSubmit(); } }}
                multiline
                maxRows={3}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleMemoSubmit}
                disabled={!newMemoContent.trim()}
                sx={{ minWidth: 60 }}
              >
                저장
              </Button>
            </Box>

            {/* 최근 메모 1개 미리보기 */}
            {isLoadingMemos ? (
              <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
            ) : memos.length > 0 ? (
              <>
                <Paper variant="outlined" sx={{ p: 2, mb: 1, background: memos[0].is_pinned ? '#fef9c3' : '#f8fafc' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
                        {memos[0].is_pinned && <span>📌</span>}
                        <Chip
                          label={MEMO_CATS[memos[0].category] || memos[0].category}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {memos[0].author_name} · {new Date(memos[0].created_at).toLocaleDateString('ko-KR')}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{memos[0].content}</Typography>
                    </Box>
                    <IconButton size="small" color="error" onClick={() => handleMemoDelete(memos[0].id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>

                {/* 더보기 / 접기 */}
                {memosTotal > 1 && (
                  <>
                    <Button
                      size="small"
                      onClick={() => setShowAllMemos(!showAllMemos)}
                      sx={{ fontSize: 12, color: 'primary.main' }}
                    >
                      {showAllMemos ? '접기' : `더보기 (${memosTotal - 1}건 더)`}
                    </Button>

                    {showAllMemos && (
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {memos.slice(1).map((memo) => (
                          <Paper key={memo.id} variant="outlined" sx={{ p: 1.5, background: memo.is_pinned ? '#fef9c3' : '#fff' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.3 }}>
                                  {memo.is_pinned && <span>📌</span>}
                                  <Chip
                                    label={MEMO_CATS[memo.category] || memo.category}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: 10 }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                                    {memo.author_name} · {new Date(memo.created_at).toLocaleDateString('ko-KR')}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{memo.content}</Typography>
                              </Box>
                              <IconButton size="small" color="error" onClick={() => handleMemoDelete(memo.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        ))}
                        {memos.length < memosTotal && (
                          <Button size="small" onClick={() => loadMemos(selectedStudentId, memos.length)}>
                            더 불러오기 ({memosTotal - memos.length}건 남음)
                          </Button>
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                등록된 메모가 없습니다.
              </Typography>
            )}
          </Paper>

          {/* ========== 주간 스케줄 섹션 (weekly 모드) ========== */}
          {scheduleType === 'weekly' && (
          <>
          <Divider sx={{ my: 4 }} />

          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <EventNoteIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                주간 스케줄
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              요일별 예상 입실/퇴실 시간을 설정합니다. 설정된 시간에 입실하지 않으면 미출석으로 처리됩니다.
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>요일</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>예상 입실</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>예상 퇴실</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">활성화</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklySchedules.map((schedule) => (
                    <TableRow
                      key={schedule.dayOfWeek}
                      sx={{ opacity: schedule.isActive ? 1 : 0.5 }}
                    >
                      <TableCell>
                        <Typography fontWeight={600}>
                          {DAYS_OF_WEEK[schedule.dayOfWeek]}요일
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="time"
                          size="small"
                          value={schedule.expectedCheckIn}
                          onChange={(e) =>
                            handleWeeklyChange(schedule.dayOfWeek, 'expectedCheckIn', e.target.value)
                          }
                          disabled={!schedule.isActive}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="time"
                          size="small"
                          value={schedule.expectedCheckOut}
                          onChange={(e) =>
                            handleWeeklyChange(schedule.dayOfWeek, 'expectedCheckOut', e.target.value)
                          }
                          disabled={!schedule.isActive}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          checked={schedule.isActive}
                          onChange={(e) =>
                            handleWeeklyChange(schedule.dayOfWeek, 'isActive', e.target.checked)
                          }
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveWeeklySchedules}
                disabled={isWeeklySaving}
              >
                {isWeeklySaving ? '저장 중...' : '주간 스케줄 저장'}
              </Button>
            </Box>
          </Paper>
          </>
          )}

          {/* ========== 스케줄 시간 섹션 ========== */}
          {/* 🔴 2026-08-18: '교시 자동 환산 표'를 걷어냈다(대표 지시). 시간으로 저장하면
              아래에 교시 체크표가 자동으로 채워져 보였는데, 원장이 그걸 '따로 설정하는 곳'으로
              오해해 더 헷갈려 했다. 표는 클릭이 막힌 보기 전용이었고 저장 버튼도 이미 꺼져 있어
              기능 손실은 없다. 판정·달력은 시간 기준 그대로다.
              ⚠️ 종전엔 이 영역 전체가 `periodTemplates.length > 0` 로 감싸여 있어
              교시 템플릿을 안 만든 매장은 '시간으로 설정' 자체가 안 보였다 — 조건도 같이 풀었다. */}
          {scheduleType === 'period' && (
          <>
              <Divider sx={{ my: 4 }} />
              <Paper sx={{ p: 3, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CalendarMonthIcon color="primary" />
                  <Typography variant="h6" fontWeight={600}>스케줄 시간</Typography>
                </Box>
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ color: '#1e40af', fontWeight: 600 }}>
                    📌 저장하면 <strong>{applyDates.effFrom}부터</strong> 이후 모든 일정에 적용됩니다.
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#475569' }}>
                    여기서 저장한 시간이 다시 바꾸기 전까지 계속 적용됩니다. 지나간 날짜는 변경되지 않습니다.
                    저장하면 학부모 앱으로 알림이 갑니다(알림톡은 매장 알림 설정에서 '스케줄 변경'을 켠 경우에만 함께 발송).
                  </Typography>
                </Box>

                {/* ⭐ 2026-06-14 승인 상태 — 대기중이면 강제 허용 버튼, 승인됐으면 승인완료+날짜 */}
                {patternStatus && (patternStatus.has_pending || patternStatus.approved_at) && (
                  <Box sx={{
                    mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid',
                    borderColor: patternStatus.has_pending ? '#fde68a' : '#bbf7d0',
                    bgcolor: patternStatus.has_pending ? '#fffbeb' : '#f0fdf4',
                  }}>
                    {patternStatus.has_pending ? (
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 600 }}>
                            🟡 스케줄 변경 요청 — 승인해야 적용됩니다
                            {' · '}
                            {patternStatus.pending_created_role === 'parent' ? '학부모가 요청' : '학생이 요청'}
                            {' · '}
                            {patternStatus.pending_parent_consented ? '학부모 동의함' : '학부모 미확인'}
                          </Typography>
                          <Button variant="contained" color="warning" size="small"
                                  disabled={forcingApprove} onClick={handleForceApprove}>
                            {forcingApprove ? '처리 중...' : '승인'}
                          </Button>
                        </Box>
                        {/* 🔴 2026-08-14: 요청 '내용'을 여기서 바로 보여준다.
                            종전엔 요청이 있다는 사실만 떠서, 원장이 무엇을 바꿔달라는 건지
                            화면 어디서도 확인할 수 없이 승인해야 했다(송파점 문의). */}
                        <ScheduleChangeTable
                          current={periodRanges}
                          next={patternStatus.pending_ranges}
                        />
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600 }}>
                        ✅ {patternStatus.approved_by_parent ? '학부모 승인 완료' : '운영자 승인 완료'}
                        {patternStatus.approved_at ? ` · ${new Date(patternStatus.approved_at).toLocaleString('ko-KR')}` : ''}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* ⭐ 2026-08-21: 승인은 났지만 아직 적용 전인 스케줄.
                    위 시간표는 '오늘 유효한 것'(출결·벌점 판정과 같은 기준)이고,
                    여기가 '언제부터 어떻게 바뀌는지'다. 둘을 한 표에 섞으면
                    화면과 실제 판정이 어긋난다. */}
                {upcoming?.effective_from && (
                  <Box sx={{
                    mb: 2, p: 1.5, borderRadius: 1.5,
                    border: '1px solid #bfdbfe', bgcolor: '#eff6ff',
                  }}>
                    <Typography variant="body2" sx={{ color: '#1d4ed8', fontWeight: 600 }}>
                      🔵 승인 완료 — <strong>{upcoming.effective_from}부터</strong> 아래대로 바뀝니다
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#475569', display: 'block', mb: 0.5 }}>
                      그날까지는 위 시간표가 그대로 적용됩니다. 오늘 출결·벌점도 위 시간표 기준입니다.
                    </Typography>
                    <ScheduleChangeTable
                      current={periodRanges}
                      next={upcoming.ranges}
                      title="바뀌는 내용"
                      nextLabel="바뀐 뒤"
                    />
                  </Box>
                )}

                {/* ⭐ 2026-06-21 운영자 시간 설정 (유일한 설정 방식, 멀티 구간) */}
                <Box sx={{ mb: 3, p: 2, borderRadius: 1.5, border: '1px solid #bfdbfe', bgcolor: '#f8fafc' }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>🕐 시간으로 설정</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    요일별 시작~종료 시간 입력. 같은 요일에 <b>여러 구간</b> 추가 가능(오전·오후 등). 빈 요일은 휴일.
                    저장하면 운영자가 승인한 것으로 처리되어 <b>{applyDates.effFrom}(오늘)부터 바로 적용</b>됩니다 — 오늘 출결·벌점 기준도 새 스케줄로 바뀝니다.
                    요일 옆 날짜가 그 요일이 처음 바뀌는 날입니다. 대기중인 학생·학부모 요청이 있으면 함께 확정됩니다.
                  </Typography>
                  {['0', '1', '2', '3', '4', '5', '6'].map((d) => (
                    <Box key={d} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ width: 62, pt: 1.2, fontWeight: 700, fontSize: 14 }}>
                        {['월', '화', '수', '목', '금', '토', '일'][Number(d)]}
                        <Box component="span" sx={{ ml: 0.5, fontSize: 11, fontWeight: 400, color: '#9ca3af' }}>
                          {applyDates.byDow[d]}~
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {(timeEditor[d] || []).length === 0 && (
                          <Typography variant="caption" sx={{ color: '#9ca3af', pt: 1.2 }}>휴일</Typography>
                        )}
                        {(timeEditor[d] || []).map((r, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <HourMinutePicker value={r.start} maxHour={24 + dayBoundaryHour}
                              onChange={(v) => updateTimeRange(d, i, 'start', v)} />
                            <span>~</span>
                            <HourMinutePicker value={r.end} maxHour={24 + dayBoundaryHour}
                              onChange={(v) => updateTimeRange(d, i, 'end', v)} />
                            <IconButton size="small" color="error" onClick={() => removeTimeRange(d, i)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button size="small" startIcon={<AddIcon />} onClick={() => addTimeRange(d)} sx={{ alignSelf: 'flex-start' }}>
                          구간 추가
                        </Button>
                      </Box>
                    </Box>
                  ))}
                  <Button variant="contained" startIcon={<SaveIcon />} disabled={savingTime}
                    onClick={handleSaveTimeSchedule} sx={{ mt: 1 }}>
                    {savingTime ? '저장 중...' : `시간 스케줄 저장 (운영자 승인 · ${applyDates.effFrom}부터 적용)`}
                  </Button>
                </Box>

              </Paper>
          </>
          )}

          {/* ========== 예외 날짜 섹션 ========== */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                📅 예외 날짜 (출석 면제)
              </Typography>
              <Button
                variant="contained"
                color="info"
                startIcon={<AddIcon />}
                onClick={() => setShowExceptionModal(true)}
                size="small"
              >
                예외 등록
              </Button>
            </Box>

            {exceptions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                등록된 예외 날짜가 없습니다.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>날짜</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>요일</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>사유</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>등록일</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">삭제</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exceptions.map((exc) => (
                      <TableRow key={exc.id}>
                        <TableCell>{formatDate(exc.exception_date)}</TableCell>
                        <TableCell>{getDayName(exc.exception_date)}</TableCell>
                        <TableCell>{exc.reason}</TableCell>
                        <TableCell>{formatDate(exc.created_at)}</TableCell>
                        <TableCell align="center">
                          <IconButton onClick={() => deleteException(exc.id)} size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {/* ========== 월간 계획 캘린더 (학생이 작성한 일별 출결) ========== */}
          {selectedStudentId && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                📅 월간 계획 (학생 작성)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                학생/학부모앱에서 작성된 일별 출결 계획 + 사유서를 캘린더로 표시합니다. 운영자가 위에서 <b>직접 저장하면 오늘부터</b>, 학생·학부모가 낸 요청을 <b>승인하면 내일부터</b> 이 달력과 출결 판정에 반영됩니다(지나간 날짜는 그대로).
              </Typography>
              <MemberMonthlyScheduleView
                key={`${selectedStudentId}-${monthlyPlanRefreshKey}`}
                studentId={selectedStudentId}
              />
            </Paper>
          )}
        </>
      )}

      {/* ========== 학원 스케줄 추가/수정 다이얼로그 ========== */}
      <Dialog open={academyDialogOpen} onClose={closeAcademyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAcademyId ? '학원 스케줄 수정' : '학원 스케줄 추가'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="학원명"
              fullWidth
              value={academyForm.academy_name}
              onChange={(e) => handleAcademyFormChange('academy_name', e.target.value)}
              placeholder="예: 영어학원, 수학학원"
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                요일 선택
              </Typography>
              <FormGroup row>
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={academyForm.days_of_week.includes(day)}
                        onChange={() => handleDayToggle(day)}
                      />
                    }
                    label={DAYS_MAP[day]}
                  />
                ))}
              </FormGroup>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="학원 시작 시간"
                type="time"
                value={academyForm.start_time}
                onChange={(e) => handleAcademyFormChange('start_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="학원 종료 시간"
                type="time"
                value={academyForm.end_time}
                onChange={(e) => handleAcademyFormChange('end_time', e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <TextField
              label="이동 시간 (분)"
              type="number"
              value={academyForm.travel_minutes}
              onChange={(e) => handleAcademyFormChange('travel_minutes', parseInt(e.target.value) || 15)}
              InputProps={{ inputProps: { min: 0, max: 120 } }}
              helperText="독서실에서 학원까지 이동하는 데 걸리는 시간입니다."
              fullWidth
            />

            <TextField
              label="여유 시간 (분)"
              type="number"
              value={academyForm.grace_minutes}
              onChange={(e) => handleAcademyFormChange('grace_minutes', parseInt(e.target.value) || 10)}
              InputProps={{ inputProps: { min: 0, max: 60 } }}
              helperText="이동 시간 + 여유 시간이 지나도 외출/복귀하지 않으면 알림이 발송됩니다."
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAcademyDialog}>취소</Button>
          <Button variant="contained" onClick={saveAcademySchedule} disabled={academySaving}>
            {academySaving ? '저장 중...' : '저장'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== 예외 날짜 등록 다이얼로그 ========== */}
      <Dialog open={showExceptionModal} onClose={() => { setShowExceptionModal(false); setExceptionDates(['']); setExceptionReason(''); setExceptionType('결석'); }} maxWidth="sm" fullWidth>
        <DialogTitle>예외 날짜 등록</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* 사유 종류 — 안 고르면 결석으로 등록된다 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                사유 종류
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['결석', '지각', '조퇴', '외출'].map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    onClick={() => setExceptionType(t)}
                    color={exceptionType === t ? 'primary' : 'default'}
                    variant={exceptionType === t ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                결석은 그 날 하루 전체를 면제합니다. 같은 날 다른 사유서가 있으면 등록되지 않습니다.
              </Typography>
            </Box>

            {/* 날짜 입력 (여러 개) */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                날짜 선택
              </Typography>
              {exceptionDates.map((date, idx) => (
                <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                  <TextField
                    type="date"
                    size="small"
                    value={date}
                    onChange={(e) => {
                      const newDates = [...exceptionDates];
                      newDates[idx] = e.target.value;
                      setExceptionDates(newDates);
                    }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  {exceptionDates.length > 1 && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setExceptionDates(exceptionDates.filter((_, i) => i !== idx));
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setExceptionDates([...exceptionDates, ''])}
                sx={{ mt: 0.5 }}
              >
                날짜 추가
              </Button>
            </Box>

            {/* 빠른 사유 버튼 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                빠른 사유 선택
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['병원', '가족행사', '개인사정', '학교행사'].map((reason) => (
                  <Chip
                    key={reason}
                    label={reason}
                    onClick={() => setExceptionReason(reason)}
                    color={exceptionReason === reason ? 'primary' : 'default'}
                    variant={exceptionReason === reason ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            {/* 사유 입력 */}
            <TextField
              label="사유"
              fullWidth
              multiline
              rows={2}
              value={exceptionReason}
              onChange={(e) => setExceptionReason(e.target.value)}
              placeholder="예외 사유를 입력하세요..."
            />

            {/* 그날 다른 사유서가 있어 결석으로 바꿔야 하는 경우 */}
            {replacePrompt && (
              <Alert
                severity="warning"
                action={
                  <Button
                    size="small"
                    color="warning"
                    variant="contained"
                    onClick={() => createExceptions(true)}
                  >
                    결석으로 바꾸기
                  </Button>
                }
              >
                {replacePrompt}
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  기존 사유서는 삭제되지 않고 '취소됨'으로 이력에 남습니다.
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowExceptionModal(false); setExceptionDates(['']); setExceptionReason(''); setExceptionType('결석'); setReplacePrompt(null); }}>
            취소
          </Button>
          <Button
            variant="contained"
            onClick={() => createExceptions()}
            disabled={exceptionDates.filter((d) => d.trim() !== '').length === 0 || !exceptionReason.trim()}
          >
            등록
          </Button>
        </DialogActions>
      </Dialog>
      </>
      )}
    </Box>
  );
}
